import { memoize } from '@/utils/memoize-util'

/**
 * Options for {@link memoizeAsync}.
 */
export interface MemoizeAsyncOptions<Args extends unknown[]> {
  /**
   * Used to locate the abort signal to use.
   * If not specified, the first argument that is an instance of `AbortSignal`
   * will be used.
   *
   * @param args
   */
  signalSelector?: (args: Args) => AbortSignal | undefined

  /**
   * Select the key to use as the cache key.
   * If not specified, defaults to the first argument.
   * @param args
   */
  keySelector?: (args: Args) => string

  /**
   * How many items to memoize.
   */
  maxCount?: number
}

/**
 * Decorator around {@link memoize} for async methods.
 */
export function memoizeAsync<const Args extends unknown[]>({
  signalSelector,
  keySelector,
  maxCount,
}: MemoizeAsyncOptions<Args> = {}) {
  return function decorator<This, Return extends PromiseLike<unknown>>(
    _originalMethod: (this: This, ...args: Args) => Return,
    context: ClassMethodDecoratorContext<
      This,
      (this: This, ...args: Args) => Return
    >,
  ) {
    const methodName = context.name

    context.addInitializer(function memoizeAsyncDecoratorInitializer() {
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
      this[methodName] = memoize(this[methodName].bind(this), {
        promise: signalSelector ?? true,
        keySelector: keySelector,
        maxCount,
      })
    })
  }
}
